import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Image as ImageIcon, Trash2, UploadCloud } from "lucide-react";
import FormField from "components/admin/FormField";
import ActionButton from "components/admin/ActionButton";
import { datePickerPopperProps } from "components/admin/datePickerPopper";
import RulesField from "./RulesField";
import ConfirmDeleteModal from "../creditsManagement/DeleteModel";
import { GiveawayContestPayload, GiveawayStatus } from "types/giveaway";
import {
  addGiveawayContest,
  deleteGiveawayContest,
  getGiveawayContestById,
  updateGiveawayContest,
} from "apis/adminServices/giveawayServices";
import { getApiErrorMessage } from "apis/adminServices/userManagmentService";
import { useToastStore } from "store/toastStore";
import {
  inputClass,
  toApiDate,
  toApiDateTime,
  toDate,
} from "./giveawayFormUtils";

const LIST_PATH = "/admin/giveaway-management";

const NAME_MAX_LENGTH = 100;
const REWARD_MAX_LENGTH = 100;
const RULES_MIN_LENGTH = 10;
const RULES_MAX_LENGTH = 2000;
const TOKEN_REWARD_MAX_VALUE = 99999;
const URL_MAX_LENGTH = 255;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
// A contest that starts and ends at the same moment can never be entered, and
// the pickers offer 15-minute slots, so one slot is the smallest sensible
// window. Enforced in three places that have to agree: the end picker's
// minimum, the check when the start moves, and submit validation.
const MIN_CONTEST_DURATION_MINUTES = 15;
const MIN_CONTEST_DURATION_MS = MIN_CONTEST_DURATION_MINUTES * 60 * 1000;
const INVALID_SOCIAL_URL_MESSAGE = "Enter a valid Instagram/LinkedIn URL.";

// Flags likely-typo'd numbers, e.g. "10,ooo" or "10ooo" — a digit run followed
// by 2+ letters commonly mistaken for digits (o/O for 0, l/I for 1). Soft
// warning only: reward text is free-form ("iPhone 15 Pro" is valid), so this
// never blocks submission.
const LOOKS_LIKE_TYPO_NUMBER = /\d[.,]?[oOlI]{2,}/;
const getRewardTypoWarning = (value: string): string | undefined =>
  LOOKS_LIKE_TYPO_NUMBER.test(value)
    ? 'This looks like it might contain letters instead of digits (e.g. "o" instead of "0") — double check the amount.'
    : undefined;

interface FormState {
  contestName: string;
  startDate: Date | null;
  endDate: Date | null;
  reward1st: string;
  reward2nd: string;
  reward3rd: string;
  additionalWinnersCount: string;
  tokenRewardAdditionalWinners: string;
  instagramUrl: string;
  linkedInUrl: string;
  rules: string;
}

const EMPTY_FORM: FormState = {
  contestName: "",
  startDate: null,
  endDate: null,
  reward1st: "",
  reward2nd: "",
  reward3rd: "",
  additionalWinnersCount: "",
  tokenRewardAdditionalWinners: "",
  instagramUrl: "",
  linkedInUrl: "",
  rules: "",
};

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];
const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp"];

const isValidImageFile = (file: File): boolean => {
  const hasValidType = ALLOWED_IMAGE_TYPES.includes(file.type.toLowerCase());
  const fileName = file.name.toLowerCase();
  const hasValidExt = ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));
  return (hasValidType || hasValidExt) && file.size <= MAX_FILE_SIZE_BYTES;
};

/** Normalizes user-entered URLs ensuring http(s):// prefix */
const normalizeUrl = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

/** Validates that the URL belongs to Instagram (e.g. instagram.com/standbytravel) */
const isValidInstagramUrl = (value: string): boolean => {
  try {
    const normalized = normalizeUrl(value);
    const url = new URL(normalized);
    const host = url.hostname.toLowerCase().replace(/^www\./, "");
    if (host !== "instagram.com" && host !== "instagr.am") {
      return false;
    }
    return url.pathname.length > 1 && url.pathname !== "/";
  } catch {
    return false;
  }
};

/** Validates that the URL belongs to LinkedIn (e.g. linkedin.com/company/standbytravel) */
const isValidLinkedInUrl = (value: string): boolean => {
  try {
    const normalized = normalizeUrl(value);
    const url = new URL(normalized);
    const host = url.hostname.toLowerCase();
    const isLinkedIn =
      host === "linkedin.com" ||
      host.endsWith(".linkedin.com") ||
      host === "lnkd.in";
    if (!isLinkedIn) {
      return false;
    }
    return url.pathname.length > 1 && url.pathname !== "/";
  } catch {
    return false;
  }
};

/**
 * Add / view-and-edit screen for a giveaway contest.
 * Mounted at `/admin/giveaway-management/new` and
 * `/admin/giveaway-management/:contestId`.
 * Finished contests get their own screen instead (`GiveawayPastContestDetails`).
 */
const GiveawayContestForm: React.FC = () => {
  const { contestId } = useParams<{ contestId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToastStore();

  const isEdit = !!contestId;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const startDatePickerRef = useRef<DatePicker>(null);
  const endDatePickerRef = useRef<DatePicker>(null);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const initialFormRef = useRef<FormState>(EMPTY_FORM);
  const initialThumbnailUrlRef = useRef<string>("");

  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [existingThumbnailUrl, setExistingThumbnailUrl] = useState("");
  const [contestStatus, setContestStatus] = useState<GiveawayStatus | null>(
    null,
  );
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormState | "thumbnail", string>>
  >({});
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const previewUrl = useMemo(() => {
    if (thumbnail) {
      return URL.createObjectURL(thumbnail);
    }
    return existingThumbnailUrl || "";
  }, [thumbnail, existingThumbnailUrl]);

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleRemoveThumbnail = () => {
    setThumbnail(null);
    setExistingThumbnailUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelected = (file: File | null) => {
    if (!file) return;
    if (!isValidImageFile(file)) {
      showToast(
        "Please upload a valid image in JPG, PNG, or WebP format. The maximum file size is 5 MB.",
        "error",
        4000,
      );
      setErrors((prev) => ({
        ...prev,
        thumbnail:
          "Please upload a valid image in JPG, PNG, or WebP format. The maximum file size is 5 MB.",
      }));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setThumbnail(file);
    setErrors((prev) => ({ ...prev, thumbnail: undefined }));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    handleFileSelected(file);
  };

  // Load the contest when the route carries an id.
  useEffect(() => {
    if (!contestId) return;

    let active = true;
    setLoading(true);
    getGiveawayContestById(Number(contestId))
      .then((contest) => {
        if (!active) return;
        if (!contest) {
          showToast("Contest not found", "error", 3000);
          navigate(LIST_PATH, { replace: true });
          return;
        }
        setContestStatus(contest.status ?? null);
        const loadedForm: FormState = {
          contestName: contest.contestName ?? "",
          startDate: toDate(contest.startDate),
          endDate: toDate(contest.endDate),
          reward1st: contest.reward1st ?? "",
          reward2nd: contest.reward2nd ?? "",
          reward3rd: contest.reward3rd ?? "",
          additionalWinnersCount:
            contest.additionalWinnersCount !== null &&
            contest.additionalWinnersCount !== undefined
              ? String(contest.additionalWinnersCount)
              : "",
          tokenRewardAdditionalWinners:
            contest.tokenRewardAdditionalWinners &&
            contest.tokenRewardAdditionalWinners > 0
              ? String(contest.tokenRewardAdditionalWinners)
              : "",
          instagramUrl: contest.instagramUrl ?? "",
          linkedInUrl: contest.linkedInUrl ?? "",
          rules: contest.rules ?? "",
        };
        setForm(loadedForm);
        initialFormRef.current = loadedForm;
        const thumbUrl = contest.thumbnailUrl ?? "";
        setExistingThumbnailUrl(thumbUrl);
        initialThumbnailUrlRef.current = thumbUrl;
      })
      .catch((error) => {
        console.error("Failed to load contest:", error);
        if (active) showToast("Failed to load contest", "error", 3000);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contestId]);

  const isStarted =
    isEdit &&
    form.startDate !== null &&
    (contestStatus === "Ongoing" ||
      contestStatus === "Awaiting Result" ||
      contestStatus === "Completed" ||
      form.startDate.getTime() <= Date.now());

  // A completed contest is read-only end to end — every field the "started"
  // rule would otherwise leave open (End Date, socials, Rules, Thumbnail,
  // Additional Winners) locks too, and only Cancel stays usable.
  const isCompleted = contestStatus === "Completed";

  // End Date & Time stays editable while still in the future; once the clock
  // reaches the originally loaded end date, it locks even if status hasn't
  // caught up to "Completed" yet.
  const isEndDateLocked =
    isCompleted ||
    (initialFormRef.current.endDate !== null &&
      initialFormRef.current.endDate.getTime() <= Date.now());

  // Post-start "add if missing" rule: these three fields stay editable after
  // the contest has started only as long as they were empty when it started —
  // once a value exists they lock like the other read-only fields.
  const reward2ndLocked =
    isStarted && !!initialFormRef.current.reward2nd.trim();
  const reward3rdLocked =
    isStarted && !!initialFormRef.current.reward3rd.trim();
  const tokenRewardAdditionalWinnersLocked =
    isStarted && !!initialFormRef.current.tokenRewardAdditionalWinners.trim();

  const isDirty = useMemo(() => {
    if (thumbnail !== null) return true;
    if (existingThumbnailUrl !== initialThumbnailUrlRef.current) return true;
    return (
      form.contestName !== initialFormRef.current.contestName ||
      form.startDate?.getTime() !==
        initialFormRef.current.startDate?.getTime() ||
      form.endDate?.getTime() !== initialFormRef.current.endDate?.getTime() ||
      form.reward1st !== initialFormRef.current.reward1st ||
      form.reward2nd !== initialFormRef.current.reward2nd ||
      form.reward3rd !== initialFormRef.current.reward3rd ||
      form.additionalWinnersCount !==
        initialFormRef.current.additionalWinnersCount ||
      form.tokenRewardAdditionalWinners !==
        initialFormRef.current.tokenRewardAdditionalWinners ||
      form.instagramUrl !== initialFormRef.current.instagramUrl ||
      form.linkedInUrl !== initialFormRef.current.linkedInUrl ||
      form.rules !== initialFormRef.current.rules
    );
  }, [form, thumbnail, existingThumbnailUrl]);

  const handleNavigateAway = () => {
    if (isDirty) {
      setConfirmingDiscard(true);
    } else {
      navigate(LIST_PATH);
    }
  };

  const setField = <K extends keyof FormState>(
    field: K,
    value: FormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev, [field]: undefined };
      if (field === "instagramUrl" || field === "linkedInUrl") {
        if (next.instagramUrl === INVALID_SOCIAL_URL_MESSAGE) {
          next.instagramUrl = undefined;
        }
        if (next.linkedInUrl === INVALID_SOCIAL_URL_MESSAGE) {
          next.linkedInUrl = undefined;
        }
      }
      return next;
    });
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState | "thumbnail", string>> = {};

    if (!isStarted) {
      if (!form.contestName.trim()) {
        next.contestName = "Contest name is required";
      } else if (form.contestName.trim().length > NAME_MAX_LENGTH) {
        next.contestName = `Contest name cannot exceed ${NAME_MAX_LENGTH} characters`;
      }

      const nowForStart = new Date();
      if (!form.startDate) {
        next.startDate = "Start date & time is required";
      } else if (form.startDate < nowForStart) {
        next.startDate = "Start date & time cannot be in the past";
      }

      if (!form.reward1st.trim()) {
        next.reward1st = "1st prize reward is required";
      } else if (form.reward1st.trim().length > REWARD_MAX_LENGTH) {
        next.reward1st = `1st prize reward cannot exceed ${REWARD_MAX_LENGTH} characters`;
      }
    }

    if (!reward2ndLocked && form.reward2nd.trim().length > REWARD_MAX_LENGTH) {
      next.reward2nd = `2nd prize reward cannot exceed ${REWARD_MAX_LENGTH} characters`;
    }

    if (!reward3rdLocked && form.reward3rd.trim().length > REWARD_MAX_LENGTH) {
      next.reward3rd = `3rd prize reward cannot exceed ${REWARD_MAX_LENGTH} characters`;
    }

    // Additional Winners Count is always editable, even after the contest starts.
    let additionalWinnersCount: number | null = null;
    if (form.additionalWinnersCount.trim()) {
      const count = Number(form.additionalWinnersCount);
      if (Number.isNaN(count) || count < 0 || !Number.isInteger(count)) {
        next.additionalWinnersCount =
          "Enter a valid number of additional winners.";
      } else {
        additionalWinnersCount = count;
      }
    }

    if (!tokenRewardAdditionalWinnersLocked) {
      if (additionalWinnersCount && additionalWinnersCount > 0) {
        if (!form.tokenRewardAdditionalWinners.trim()) {
          next.tokenRewardAdditionalWinners = "Enter a valid token value.";
        } else {
          const tokens = Number(form.tokenRewardAdditionalWinners);
          if (Number.isNaN(tokens) || tokens < 1 || !Number.isInteger(tokens)) {
            next.tokenRewardAdditionalWinners = "Enter a valid token value.";
          } else if (tokens > TOKEN_REWARD_MAX_VALUE) {
            next.tokenRewardAdditionalWinners = `Token reward cannot exceed ${TOKEN_REWARD_MAX_VALUE.toLocaleString()}`;
          }
        }
      } else if (form.tokenRewardAdditionalWinners.trim()) {
        const tokens = Number(form.tokenRewardAdditionalWinners);
        if (Number.isNaN(tokens) || tokens < 1 || !Number.isInteger(tokens)) {
          next.tokenRewardAdditionalWinners = "Enter a valid token value.";
        }
      }
    }

    const now = new Date();
    const endDateChanged =
      form.endDate?.getTime() !== initialFormRef.current.endDate?.getTime();
    // Past end dates are only allowed when left untouched (e.g. natural
    // clock drift on an active contest) — any actual edit must land in the future.
    const endDateMustBeFuture = !isEdit || endDateChanged;
    if (!form.endDate) {
      next.endDate = "End date & time is required";
    } else if (form.startDate) {
      if (
        form.endDate.getTime() - form.startDate.getTime() <
        MIN_CONTEST_DURATION_MS
      ) {
        next.endDate = `End date & time must be at least ${MIN_CONTEST_DURATION_MINUTES} minutes after the start`;
      } else if (endDateMustBeFuture && form.endDate <= now) {
        next.endDate = "End date & time cannot be in the past";
      }
    } else if (endDateMustBeFuture && form.endDate <= now) {
      next.endDate = "End date & time cannot be in the past";
    }

    const hasInstagram = !!form.instagramUrl.trim();
    const hasLinkedIn = !!form.linkedInUrl.trim();

    if (!hasInstagram && !hasLinkedIn) {
      next.instagramUrl = INVALID_SOCIAL_URL_MESSAGE;
      next.linkedInUrl = INVALID_SOCIAL_URL_MESSAGE;
    } else {
      if (hasInstagram) {
        if (form.instagramUrl.trim().length > URL_MAX_LENGTH) {
          next.instagramUrl = `URL cannot exceed ${URL_MAX_LENGTH} characters`;
        } else if (!isValidInstagramUrl(form.instagramUrl)) {
          next.instagramUrl = INVALID_SOCIAL_URL_MESSAGE;
        }
      }
      if (hasLinkedIn) {
        if (form.linkedInUrl.trim().length > URL_MAX_LENGTH) {
          next.linkedInUrl = `URL cannot exceed ${URL_MAX_LENGTH} characters`;
        } else if (!isValidLinkedInUrl(form.linkedInUrl)) {
          next.linkedInUrl = INVALID_SOCIAL_URL_MESSAGE;
        }
      }
    }

    if (!form.rules.trim()) {
      next.rules = "Rules cannot be empty.";
    } else if (form.rules.trim().length < RULES_MIN_LENGTH) {
      next.rules = `Rules must be at least ${RULES_MIN_LENGTH} characters.`;
    } else if (form.rules.trim().length > RULES_MAX_LENGTH) {
      next.rules = `Rules cannot exceed ${RULES_MAX_LENGTH} characters.`;
    }

    if (!isEdit && !thumbnail) {
      next.thumbnail =
        "Please upload a valid image in JPG, PNG, or WebP format. The maximum file size is 5 MB.";
    } else if (isEdit && !thumbnail && !existingThumbnailUrl) {
      next.thumbnail =
        "Please upload a valid image in JPG, PNG, or WebP format. The maximum file size is 5 MB.";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const reward2nd = form.reward2nd.trim();
    const reward3rd = form.reward3rd.trim();

    const payload: GiveawayContestPayload = {
      ...(contestId ? { contestId: Number(contestId) } : {}),
      contestName: form.contestName.trim(),
      startDate: toApiDateTime(form.startDate),
      endDate: toApiDateTime(form.endDate),
      reward1st: form.reward1st.trim(),
      ...(reward2nd ? { reward2nd } : {}),
      ...(reward3rd ? { reward3rd } : {}),
      additionalWinnersCount:
        form.additionalWinnersCount === ""
          ? null
          : Number(form.additionalWinnersCount),
      tokenRewardAdditionalWinners:
        form.tokenRewardAdditionalWinners === ""
          ? null
          : Number(form.tokenRewardAdditionalWinners),
      instagramUrl: normalizeUrl(form.instagramUrl),
      linkedinUrl: normalizeUrl(form.linkedInUrl),
      rules: form.rules.trim(),
      resultAnnouncementDate: form.endDate
        ? toApiDateTime(form.endDate)
        : toApiDateTime(new Date()),
      thumbnail,
      thumbnailUrl: existingThumbnailUrl || undefined,
    };

    setSubmitting(true);
    try {
      if (contestId) {
        await updateGiveawayContest(payload);
        showToast("Contest updated successfully", "success", 3000);
      } else {
        await addGiveawayContest(payload);
        showToast("Contest added successfully", "success", 3000);
      }
      navigate(LIST_PATH);
    } catch (error) {
      console.error("Failed to save contest:", error);
      showToast(
        getApiErrorMessage(
          error,
          isEdit ? "Failed to update contest" : "Failed to add contest",
        ),
        "error",
        4000,
      );
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Soft delete — the contest disappears from both lists but its winner rows
   * are kept for audit, and any tokens already credited stay with the users.
   */
  const handleDelete = async () => {
    if (!contestId) return;

    setDeleting(true);
    try {
      await deleteGiveawayContest(Number(contestId));
      showToast("Contest deleted successfully", "success", 3000);
      navigate(LIST_PATH);
    } catch (error) {
      console.error("Failed to delete contest:", error);
      showToast(
        getApiErrorMessage(error, "Failed to delete contest"),
        "error",
        3000,
      );
    } finally {
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  const handleDeleteClick = () => {
    if (isStarted) {
      showToast(
        "This contest has already started and can no longer be deleted.",
        "error",
        4000,
      );
      return;
    }
    setConfirmingDelete(true);
  };

  const cleanExistingFileName = (url: string): string => {
    if (!url) return "";
    try {
      const pathname = url.split("?")[0];
      const filename = pathname.split("/").pop() || "";
      return decodeURIComponent(filename);
    } catch {
      return url.split("?")[0].split("/").pop() || "";
    }
  };

  const thumbnailLabel =
    thumbnail?.name || cleanExistingFileName(existingThumbnailUrl);

  // --- Field blocks, ordered per mode further down ---

  const contestNameField = (
    <FormField
      label="Contest Name"
      htmlFor="contestName"
      required
      error={errors.contestName}
    >
      <input
        id="contestName"
        type="text"
        maxLength={NAME_MAX_LENGTH}
        value={form.contestName}
        onChange={(e) => setField("contestName", e.target.value)}
        placeholder="e.g. Summer Travel Giveaway 2026"
        disabled={isStarted || submitting}
        className={inputClass}
      />
    </FormField>
  );

  /** Reward text field for one prize tier (1st/2nd/3rd). */
  const prizeRow = (place: 1 | 2 | 3) => {
    const rewardField =
      place === 1 ? "reward1st" : place === 2 ? "reward2nd" : "reward3rd";
    const label =
      place === 1 ? "1st Prize" : place === 2 ? "2nd Prize" : "3rd Prize";
    const rewardPlaceholder =
      place === 1
        ? "e.g. Casio Watch, iPhone 15 Pro"
        : place === 2
          ? "e.g. Travel Voucher, Headphones"
          : "e.g. Gift Card, Backpack";
    const locked =
      place === 2 ? reward2ndLocked : place === 3 ? reward3rdLocked : isStarted;

    return (
      <FormField
        label={`Contest Reward ${label}`}
        htmlFor={rewardField}
        required={place === 1}
      >
        <textarea
          id={rewardField}
          rows={2}
          maxLength={REWARD_MAX_LENGTH}
          value={form[rewardField]}
          onChange={(e) => setField(rewardField, e.target.value)}
          placeholder={rewardPlaceholder}
          disabled={locked || submitting}
          className={`${inputClass} resize-y min-h-[56px] max-h-[160px]`}
        />
        {errors[rewardField] && (
          <p className="text-xs text-red-500 mt-1">{errors[rewardField]}</p>
        )}
      </FormField>
    );
  };

  const additionalWinnersFields = (
    <div className="grid md:grid-cols-2 gap-x-6 gap-y-6">
      <FormField
        label="Additional Winners Count"
        htmlFor="additionalWinnersCount"
        error={errors.additionalWinnersCount}
      >
        <input
          id="additionalWinnersCount"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={4}
          value={form.additionalWinnersCount}
          onKeyDown={(e) => {
            if (["-", "+", "e", "E", "."].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onChange={(e) => {
            const digitsOnly = e.target.value.replace(/\D/g, "");
            if (digitsOnly.length <= 4) {
              setField("additionalWinnersCount", digitsOnly);
            }
          }}
          placeholder="e.g. 5"
          disabled={submitting || isCompleted}
          className={inputClass}
        />
        <p className="text-[11px] text-lightgray-200 mt-1">
          Number of extra winners that can be published for this contest, beyond
          1st/2nd/3rd Prize.
        </p>
      </FormField>

      <FormField
        label="Token Reward – Additional Winners"
        htmlFor="tokenRewardAdditionalWinners"
        required={
          !!form.additionalWinnersCount.trim() &&
          Number(form.additionalWinnersCount) > 0
        }
        error={errors.tokenRewardAdditionalWinners}
      >
        <input
          id="tokenRewardAdditionalWinners"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={5}
          value={form.tokenRewardAdditionalWinners}
          onKeyDown={(e) => {
            if (["-", "+", "e", "E", "."].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onChange={(e) => {
            const digitsOnly = e.target.value.replace(/\D/g, "");
            if (digitsOnly.length <= 5) {
              setField("tokenRewardAdditionalWinners", digitsOnly);
            }
          }}
          placeholder="e.g. 100"
          disabled={tokenRewardAdditionalWinnersLocked || submitting || isCompleted}
          className={inputClass}
        />
        <p className="text-[11px] text-lightgray-200 mt-1">
          Applied to every Additional Winner published for this contest.
        </p>
      </FormField>
    </div>
  );

  const contestPeriodField = (() => {
    const today = new Date();
    // Fall back to the date the calendar defaults to when nothing is
    // selected yet, so restrictions are active on the very first click
    // that lands on today (or on the start day) — not only once state has
    // caught up on a later render.
    const effectiveStartDate = form.startDate ?? today;
    const effectiveEndDate = form.endDate ?? form.startDate ?? today;
    const isStartToday = effectiveStartDate.toDateString() === today.toDateString();

    const isEndSameAsStart =
      !!form.startDate &&
      effectiveEndDate.toDateString() === form.startDate.toDateString();
    const isEndToday = effectiveEndDate.toDateString() === today.toDateString();

    return (
      <FormField label="Contest Period" required>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <div
              className={`relative rounded-lg border border-[#D1D5DB] px-3 py-2 ${
                isStarted ? "bg-gray-100 cursor-not-allowed" : "cursor-pointer"
              }`}
              onClick={() => {
                if (!isStarted) startDatePickerRef.current?.setOpen(true);
              }}
            >
              <div className="flex items-center justify-between">
                <p
                  className={`text-xs font-semibold ${
                    isStarted ? "text-gray-500" : "text-grease-100"
                  }`}
                >
                  Start Date &amp; Time
                </p>
                {!isStarted && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      startDatePickerRef.current?.setOpen(true);
                    }}
                    className="st-calendar text-lightgray-200 text-sm hover:text-grease-100 cursor-pointer"
                    aria-label="Open start date picker"
                  />
                )}
              </div>
              <DatePicker
                {...datePickerPopperProps}
                popperClassName={`${datePickerPopperProps.popperClassName} st-giveaway-datepicker`}
                ref={startDatePickerRef}
                id="startDate"
                selected={form.startDate}
                disabled={isStarted || submitting}
                onChange={(date) => {
                  if (date) {
                    let finalDate = date;
                    const now = new Date();
                    const isToday = date.toDateString() === now.toDateString();

                    if (!form.startDate && !isToday) {
                      finalDate = new Date(date);
                      finalDate.setHours(0, 0, 0, 0);
                    }

                    // A bare day-click (or a stale time from a previously picked later
                    // day) can land at/before now for today — clamp forward to the next
                    // 15-min slot strictly after now, so it never locks via isStarted.
                    if (isToday && finalDate.getTime() <= now.getTime()) {
                      finalDate = new Date(date);
                      const nextSlotMinutes =
                        Math.floor(now.getMinutes() / 15) * 15 + 15;
                      finalDate.setHours(now.getHours(), nextSlotMinutes, 0, 0);
                    }

                    setField("startDate", finalDate);
                    // Clears an end that no longer leaves the minimum window,
                    // rather than leaving a combination the form will reject on
                    // submit without saying which field moved.
                    if (
                      form.endDate &&
                      form.endDate.getTime() - finalDate.getTime() <
                        MIN_CONTEST_DURATION_MS
                    ) {
                      setField("endDate", null);
                    }
                  } else {
                    setField("startDate", null);
                  }
                }}
                showTimeSelect
                timeIntervals={15}
                dateFormat="dd/MM/yyyy HH:mm"
                timeFormat="HH:mm"
                minDate={today}
                openToDate={form.startDate ?? today}
                minTime={isStartToday ? today : undefined}
                maxTime={
                  isStartToday
                    ? new Date(new Date().setHours(23, 59, 59, 999))
                    : undefined
                }
                placeholderText="dd/mm/yyyy"
                className={`w-full p-0 border-0 text-sm ${
                  isStarted
                    ? "text-gray-500 bg-gray-100 cursor-not-allowed"
                    : "text-grease-200 cursor-pointer"
                } focus:ring-0 focus:outline-none`}
                wrapperClassName="w-full"
                onFocus={(e) => e.target.setAttribute("autocomplete", "off")}
              />
            </div>
            {errors.startDate && (
              <p className="text-xs text-red-500 mt-1.5">{errors.startDate}</p>
            )}
          </div>

          <div className="flex-1">
            <div
              className={`relative rounded-lg border border-[#D1D5DB] px-3 py-2 ${
                isEndDateLocked ? "bg-gray-100 cursor-not-allowed" : "cursor-pointer"
              }`}
              onClick={() => {
                if (!isEndDateLocked) endDatePickerRef.current?.setOpen(true);
              }}
            >
              <div className="flex items-center justify-between">
                <p
                  className={`text-xs font-semibold ${
                    isEndDateLocked ? "text-gray-500" : "text-grease-100"
                  }`}
                >
                  End Date &amp; Time
                </p>
                {!isEndDateLocked && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      endDatePickerRef.current?.setOpen(true);
                    }}
                    className="st-calendar text-lightgray-200 text-sm hover:text-grease-100 cursor-pointer"
                    aria-label="Open end date picker"
                  />
                )}
              </div>
              <DatePicker
                {...datePickerPopperProps}
                popperClassName={`${datePickerPopperProps.popperClassName} st-giveaway-datepicker`}
                ref={endDatePickerRef}
                id="endDate"
                selected={form.endDate}
                disabled={submitting || isEndDateLocked}
                onChange={(date) => {
                  if (date) {
                    let finalDate = date;
                    const rightNow = new Date();
                    const isToday = date.toDateString() === rightNow.toDateString();
                    const isSameAsStart =
                      !!form.startDate &&
                      date.toDateString() === form.startDate.toDateString();

                    if (!form.endDate && !isToday && !isSameAsStart) {
                      finalDate = new Date(date);
                      finalDate.setHours(0, 0, 0, 0);
                    }

                    // A bare day-click (or a stale time carried over from a
                    // previous selection) skips the time list's disabled-time
                    // check, so it can land at/before now or before the
                    // minimum gap from the start — clamp forward to the next
                    // valid 15-min slot so it never violates those rules.
                    const bounds: number[] = [];
                    if (isToday) bounds.push(rightNow.getTime());
                    if (isSameAsStart && form.startDate) {
                      bounds.push(form.startDate.getTime() + MIN_CONTEST_DURATION_MS);
                    }
                    if (bounds.length) {
                      const minAllowed = new Date(Math.max(...bounds));
                      if (finalDate.getTime() < minAllowed.getTime()) {
                        const nextSlotMinutes =
                          Math.floor(minAllowed.getMinutes() / 15) * 15 + 15;
                        finalDate = new Date(date);
                        finalDate.setHours(minAllowed.getHours(), nextSlotMinutes, 0, 0);
                      }
                    }

                    setField("endDate", finalDate);
                  } else {
                    setField("endDate", null);
                  }
                }}
                showTimeSelect
                timeIntervals={15}
                dateFormat="dd/MM/yyyy HH:mm"
                timeFormat="HH:mm"
                minDate={
                  form.startDate && form.startDate.getTime() > today.getTime()
                    ? form.startDate
                    : today
                }
                openToDate={form.endDate ?? form.startDate ?? today}
                minTime={(() => {
                  const bounds: number[] = [];
                  if (isEndToday) bounds.push(new Date().getTime());
                  if (isEndSameAsStart && form.startDate) {
                    bounds.push(form.startDate.getTime() + MIN_CONTEST_DURATION_MS);
                  }
                  return bounds.length ? new Date(Math.max(...bounds)) : undefined;
                })()}
                maxTime={
                  isEndSameAsStart || isEndToday
                    ? new Date(new Date().setHours(23, 59, 59, 999))
                    : undefined
                }
                placeholderText="dd/mm/yyyy"
                className={`w-full p-0 border-0 text-sm ${
                  isEndDateLocked
                    ? "text-gray-500 bg-gray-100 cursor-not-allowed"
                    : "text-grease-200 cursor-pointer"
                } focus:ring-0 focus:outline-none`}
                wrapperClassName="w-full"
                onFocus={(e) => e.target.setAttribute("autocomplete", "off")}
              />
            </div>
            {errors.endDate && (
              <p className="text-xs text-red-500 mt-1.5">{errors.endDate}</p>
            )}
          </div>
        </div>
      </FormField>
    );
  })();

  const socialMediaUrlField = (
    <div>
      <p className="text-sm font-semibold text-grease-100 mb-3">
        Social Media URL<span className="text-red-500 ml-0.5">*</span>
      </p>

      <div className="flex items-center gap-3">
        <label
          htmlFor="instagramUrl"
          className="w-28 shrink-0 text-sm text-grease-100"
        >
          Instagram URL
        </label>
        <input
          id="instagramUrl"
          type="url"
          maxLength={URL_MAX_LENGTH}
          value={form.instagramUrl}
          onChange={(e) => setField("instagramUrl", e.target.value)}
          placeholder="instagram.com/standby.travel"
          disabled={submitting || isCompleted}
          className={`${inputClass} p-2`}
        />
      </div>

      <div className="flex items-center gap-3 mt-3">
        <label
          htmlFor="linkedInUrl"
          className="w-28 shrink-0 text-sm text-grease-100"
        >
          LinkedIn URL
        </label>
        <input
          id="linkedInUrl"
          type="url"
          maxLength={URL_MAX_LENGTH}
          value={form.linkedInUrl}
          onChange={(e) => setField("linkedInUrl", e.target.value)}
          placeholder="linkedin.com/company/standbytravel"
          disabled={submitting || isCompleted}
          className={`${inputClass} p-2`}
        />
      </div>
      {(errors.instagramUrl || errors.linkedInUrl) && (
        <p className="text-xs text-red-500 mt-1.5 ml-[7.75rem]">
          {errors.instagramUrl || errors.linkedInUrl}
        </p>
      )}
    </div>
  );

  const rulesFieldBlock = (
    <FormField label="Rules" htmlFor="rules" required>
      <RulesField
        value={form.rules}
        onChange={(value) => setField("rules", value)}
        disabled={submitting || isCompleted}
        error={errors.rules}
        maxLength={RULES_MAX_LENGTH}
      />
    </FormField>
  );

  const thumbnailField = (
    <FormField
      label="Thumbnail Image"
      required={!isEdit}
      error={errors.thumbnail}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0] ?? null;
          handleFileSelected(file);
        }}
      />
      {previewUrl ? (
        <div
          className={`relative rounded-lg border p-3 bg-gray-50 flex items-center justify-between gap-3 min-w-0 ${
            errors.thumbnail ? "border-red-500" : "border-[#D1D5DB]"
          }`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={previewUrl}
              alt="Thumbnail preview"
              className="w-14 h-14 object-cover rounded-md border border-gray-200 shrink-0 bg-white"
            />
            <div className="min-w-0">
              <p className="text-sm font-medium text-grease-100 truncate">
                {thumbnailLabel || "thumbnail-image"}
              </p>
              <p className="text-xs text-lightgray-200">
                {thumbnail
                  ? `${(thumbnail.size / 1024).toFixed(1)} KB`
                  : "Current Thumbnail"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting || isCompleted}
              className="px-3 py-1.5 text-xs font-medium text-light-yellow hover:bg-yellow-50 rounded-md transition-colors border border-light-yellow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Change
            </button>
            <button
              type="button"
              onClick={handleRemoveThumbnail}
              disabled={submitting || isCompleted}
              className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Remove image"
              aria-label="Remove image"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => {
            if (!isCompleted) fileInputRef.current?.click();
          }}
          onDragOver={isCompleted ? undefined : handleDragOver}
          onDragLeave={isCompleted ? undefined : handleDragLeave}
          onDrop={isCompleted ? undefined : handleDrop}
          className={`border-2 border-dashed rounded-lg p-5 flex flex-col items-center justify-center transition-colors text-center ${
            isCompleted
              ? "cursor-not-allowed opacity-60 border-[#D1D5DB] bg-gray-50/50"
              : `cursor-pointer ${
                  errors.thumbnail
                    ? "border-red-500 bg-red-50/10"
                    : isDragging
                      ? "border-light-yellow bg-yellow-50/40"
                      : "border-[#D1D5DB] hover:border-light-yellow bg-gray-50/50 hover:bg-yellow-50/20"
                }`
          }`}
        >
          <UploadCloud
            className="w-7 h-7 text-gray-400 mb-2"
            aria-hidden="true"
          />
          <p className="text-sm text-grease-100">
            <span className="font-semibold text-light-yellow">
              Click to upload
            </span>{" "}
            or drag and drop
          </p>
          <p className="text-xs text-lightgray-200 mt-1">
            JPG, PNG, WebP up to 5 MB
          </p>
        </div>
      )}
    </FormField>
  );

  return (
    <>
      {/* Page heading card */}
      <section className="bg-white px-4 py-1.5 md:px-6 md:py-1.5 rounded-xl flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleNavigateAway}
            className="w-8 h-8 rounded-lg border border-[#D1D5DB] flex items-center justify-center text-grease-100 hover:bg-gray-50 transition-colors"
            title="Back to Giveaway Management"
            aria-label="Back to Giveaway Management"
          >
            <i className="st-arrow-left text-xs" aria-hidden="true" />
          </button>
          <h1 className="text-xl font-bold text-grease-100 tracking-tight">
            Giveaway Management
          </h1>
        </div>
      </section>

      <div className="bg-white p-4 md:p-6 rounded-xl mt-4">
        {loading ? (
          <p className="text-center py-12 text-lightgray-200 text-sm">
            Loading...
          </p>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {isStarted && (
              <div className="mb-6 p-4 rounded-lg bg-blue-50 border border-blue-200 flex items-center gap-3 text-sm text-royalblue-100">
                <i className="st-info text-base shrink-0" aria-hidden="true" />
                <span>
                  {isCompleted
                    ? "This contest has ended. All fields are read-only."
                    : "This contest has already started. Only limited fields can now be edited."}
                </span>
              </div>
            )}

            {/* No `grid-cols-1`: assets/css/style.css ships a stale
                unconditional `.grid-cols-1` that loads after Tailwind and would
                beat `md:grid-cols-2` at every width. A bare grid is already
                single-column, so the override never gets a chance to bite.
                Columns are explicit rather than row-major. */}
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
              <div className="min-w-0">
                {contestNameField}
                <div className="mt-1.5">{contestPeriodField}</div>
                <div className="space-y-1 mt-1.5">
                  {prizeRow(1)}
                  {prizeRow(2)}
                  {prizeRow(3)}
                </div>
                <div className="mt-4">{additionalWinnersFields}</div>
              </div>

              <div className="flex flex-col space-y-4 min-w-0">
                {socialMediaUrlField}
                {rulesFieldBlock}
                {thumbnailField}

                <div className="flex flex-col-reverse md:flex-row md:justify-end gap-3 mt-auto pt-12">
                  <ActionButton
                    variant="secondary"
                    onClick={handleNavigateAway}
                    disabled={submitting || deleting}
                  >
                    Cancel
                  </ActionButton>
                  {/* Nothing to delete until the contest exists; hidden entirely once completed. */}
                  {isEdit && !isCompleted && (
                    <ActionButton
                      variant="danger"
                      onClick={handleDeleteClick}
                      disabled={isStarted || submitting || deleting}
                      title={
                        isStarted
                          ? "This contest has already started and can no longer be deleted."
                          : undefined
                      }
                      loading={deleting}
                    >
                      Delete Contest
                    </ActionButton>
                  )}
                  {!isCompleted && (
                    <ActionButton
                      type="submit"
                      loading={submitting}
                      disabled={deleting}
                    >
                      {isEdit ? "Update Contest" : "Save Contest"}
                    </ActionButton>
                  )}
                </div>
              </div>
            </div>
          </form>
        )}
      </div>

      <ConfirmDeleteModal
        isOpen={confirmingDelete}
        title="Delete Contest"
        message={
          <p className="text-sm text-gray-700 leading-relaxed">
            Are you sure you want to delete{" "}
            <strong className="font-semibold text-grease-100">
              "{form.contestName}"
            </strong>
            ? This action cannot be undone.
          </p>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isDestructive={true}
        onConfirm={handleDelete}
        onCancel={() => setConfirmingDelete(false)}
        configurationName={form.contestName}
      />

      <ConfirmDeleteModal
        isOpen={confirmingDiscard}
        title="Discard unsaved changes?"
        message={
          <p className="text-sm text-gray-700 leading-relaxed">
            You have unsaved changes. Are you sure you want to leave this page?
          </p>
        }
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        isDestructive={true}
        onConfirm={() => {
          setConfirmingDiscard(false);
          navigate(LIST_PATH);
        }}
        onCancel={() => setConfirmingDiscard(false)}
        configurationName=""
      />
    </>
  );
};

export default GiveawayContestForm;
